/**
 * Per-route title, description, canonical URL, and Open Graph / Twitter tags.
 */

import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { absolutePublicUrl } from '../../utils/publicSite'

const SITE_NAME = 'TCGEasy'
const DEFAULT_DESCRIPTION =
  'Smart inventory for trading card retailers — automated pricing, multi-platform sync, and analytics.'
const DEFAULT_OG_IMAGE_PATH = '/images/og/tcgeasy-share.png'

export interface PublicPageMetaProps {
  title: string
  description?: string
  /** Path to image under public/ or absolute URL */
  ogImagePath?: string
  noIndex?: boolean
}

function stripSiteSuffix(title: string): string {
  const t = title.trim()
  if (t.toLowerCase().includes('tcgeasy')) return t
  return `${t} | ${SITE_NAME}`
}

export function PublicPageMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  ogImagePath = DEFAULT_OG_IMAGE_PATH,
  noIndex,
}: PublicPageMetaProps) {
  const location = useLocation()
  const canonicalPath = location.pathname.replace(/\/+$/, '') || '/'
  const canonicalUrl = absolutePublicUrl(canonicalPath)
  const pageTitle = stripSiteSuffix(title)
  const imageUrl = ogImagePath.startsWith('http') ? ogImagePath : absolutePublicUrl(ogImagePath)

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {noIndex ? <meta name="robots" content="noindex,nofollow" /> : null}
    </Helmet>
  )
}
